import React, { useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import App from './App';
import { ProtectedRoute } from './middleware/ProtectedRoute';
import Dashboard from './Dashboard';
import Match from './Match';
import Training from './Training';
import Leaderboard from './Leaderboard';
import { isLeaderboardHidden } from './utils/teamSettings';
import Profile from './MyProfile';
import CreateMatch from './CreateMatch';
import TeamSelection from './components/TeamSelection';
import ForgotPassword from './ForgotPassword';
import ResetPassword from './ResetPassword';
import Payments from './Payments';
import PlayerCredits from './PlayerCredits';
import Register from './Register';
import Admin from './Admin';
import TeamSelectionPage from './TeamSelectionPage';
import PitchList from './PitchList';
import ScrollToTop from './ScrollToTop';
import PitchDetails from './PitchDetails';
import Fees from './Fees';
import PlayerMatches from './PlayerMatches';
import Players from './Players';
import ActivityLog from './ActivityLog';
import Matches from './components/Matches';
import CacheBuster from './config/CacheBuster';
import CreateTrainingSession from './CreateTrainingSession';
import TrainingSchedule from './TrainingSchedule';
import AuthTest from './components/AuthTest';
import PaymentSuccess from './PaymentSuccess';
import ManagePlayers from './Players';
import PushNotificationAdmin from './components/PushNotificationAdmin';
import PushNotificationTester from './components/PushNotificationTester';
import RegisterTeam from './components/admin/RegisterTeam';
import RegisterPlayer from './components/admin/RegisterPlayer';
import AssignManagerCoach from './components/admin/AssignManagerCoach';
import AssignClubSecretary from './components/admin/AssignClubSecretary';
import MyAccount from './MyAccount';
import Notifications from './components/Notifications';
import StoryManager from './admin/StoryManager';
import ProductNewsManager from './admin/ProductNewsManager';
import TeamSettings from './admin/TeamSettings';
import AcademySubscriptions from './admin/AcademySubscriptions';
import AcademySubscriptionEdit from './admin/AcademySubscriptionEdit';
import AcademyPayments from './admin/AcademyPayments';
import AcademyTreasurerReports from './admin/AcademyTreasurerReports';
import ParentArticleView from './components/parent/ParentArticleView';
import ParentArticlesIndex from './components/parent/ParentArticlesIndex';
import Help from './Help';
import TeamPage from './TeamPage';
import EditTeam from './EditTeam';
import { authService } from './services/authService';
import api from './services/apiService';
import { getCachedAcademies, setCachedAcademies, ACADEMY_ADMIN_API } from './utils/academyAccess';
import './utils/userRole';
import RegisterTeamForm from './components/RegisterTeam';
// Import notification provider
import { NotificationProvider } from './components/NotificationProvider';
import PushReminderHost from './components/PushReminderHost';
import PaymentReturn from './components/PaymentReturn';
import StripeAdmin from './components/admin/StripeAdmin';
import SeasonManagement from './components/admin/SeasonManagement';
import BottomNav from './components/BottomNav';
import TeamDirectory from './TeamDirectory';
import PlayerAttendance from './components/PlayerAttendance';
import MembershipPlayers from './components/MembershipPlayers';
import MembershipParent from './components/MembershipParent';
import MatchReportsPage from './MatchReportsPage';
import ParentSettings from './ParentSettings';
import ParentRegister from './register/ParentRegister';
import AcademyWelcomePage from './AcademyWelcomePage';
import FriendlyWelcomePage from './FriendlyWelcomePage';
import MatchWelcomePage from './MatchWelcomePage';
import PlayerSignOnPage from './PlayerSignOnPage';
import PlayerSignOnListPage from './PlayerSignOnListPage';
import ProSignupPage from './ProSignupPage';

// Component to conditionally render BottomNav
const ConditionalBottomNav = () => {
  const location = useLocation();
  
  // Hide BottomNav on public onboarding pages and login pages
  const hideBottomNavPaths = ['/academy-welcome', '/friendly-welcome', '/match-welcome', '/player-sign-on', '/pro-signup', '/pro-signup/parent'];
  
  // Check if we're on a login page (root, login, or team-specific route)
  const isLoginPage = location.pathname === '/' || 
                     location.pathname === '/login' || 
                     (location.pathname.length > 1 && /^\/\d+$/.test(location.pathname));
  
  if (hideBottomNavPaths.includes(location.pathname) || isLoginPage) {
    return null;
  }
  
  return <BottomNav />;
};

// Admin route wrapper components
const AdminRouteWrapper = ({ component: Component }) => {
  const isAdminUser = authService.isAdmin();
  const isManager = authService.isManager();
  const [allowed, setAllowed] = useState(
    isAdminUser || isManager || getCachedAcademies().length > 0
  );
  const [checked, setChecked] = useState(isAdminUser || isManager);

  useEffect(() => {
    if (isAdminUser || isManager) {
      setAllowed(true);
      setChecked(true);
      return;
    }
    if (Component) {
      setAllowed(false);
      setChecked(true);
      return;
    }
    let cancelled = false;
    api.get(`${ACADEMY_ADMIN_API}/get_my_academy_subscriptions.php`)
      .then((response) => {
        if (cancelled) {
          return;
        }
        const list = response.data?.success ? (response.data.academies || []) : [];
        setCachedAcademies(list);
        setAllowed(list.length > 0);
        setChecked(true);
      })
      .catch(() => {
        if (!cancelled) {
          setAllowed(getCachedAcademies().length > 0);
          setChecked(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [isAdminUser, isManager, Component]);

  if (!checked) {
    return null;
  }
  if (!allowed) {
    return <Navigate to="/dashboard" replace />;
  }
  if (Component) {
    return <Component />;
  }
  return <Admin />;
};

const CreateMatchWrapper = () => {
  const isAdmin = authService.isAdmin();
  const isManager = authService.isManager();
  
  if (isAdmin || isManager) {
    return <CreateMatch />;
  } else {
    return <Navigate to="/dashboard" replace />;
  }
};

const TrainingSessionWrapper = () => {
  const isAdmin = authService.isAdmin();
  const isManager = authService.isManager();

  if (isAdmin || isManager) {
    return <CreateTrainingSession />;
  } else {
    return <Navigate to="/dashboard" replace />;
  }
};

const TrainingScheduleWrapper = () => {
  const isAdmin = authService.isAdmin();
  const isManager = authService.isManager();
  const isParent = authService.isParent();
  if (isAdmin || isManager || isParent) {
    return <TrainingSchedule />;
  } else {
    return <Navigate to="/dashboard" replace />;
  }
};

const PlayersWrapper = () => {
  const isAdmin = authService.isAdmin();
  const isManager = authService.isManager();
  
  if (isAdmin || isManager) {
    return <Players />;
  } else {
    return <Navigate to="/dashboard" replace />;
  }
};

const TeamSelectionWrapper = () => {
  const isAdmin = authService.isAdmin();
  
  if (isAdmin) {
    return <TeamSelection />;
  } else {
    return <Navigate to="/dashboard" replace />;
  }
};

const PlayerMatchesWrapper = () => {
  return authService.isAdmin() ? <PlayerMatches /> : <Navigate to="/dashboard" replace />;
};

const StoryManagerWrapper = () => {
  return authService.isAdmin() ? <StoryManager /> : <Navigate to="/dashboard" replace />;
};

const SuperAdminWrapper = ({ component: Component }) => {
  const isSuperAdmin = authService.isSuperAdmin();
  
  if (isSuperAdmin) {
    return <Component />;
  } else {
    return <Navigate to="/dashboard" replace />;
  }
};

const AssignManagerCoachWrapper = () => {
  if (authService.isSuperAdmin() || authService.isManager()) {
    return <AssignManagerCoach />;
  }
  return <Navigate to="/admin" replace />;
};

const TeamDirectoryWrapper = () => {
  const userType = sessionStorage.getItem('user_type');
  const userId = sessionStorage.getItem('user_id');
  
  // Allow coaches and super admin (user ID 1)
  if (userType === 'coach' || userId === '1') {
    return <TeamDirectory />;
  } else {
    return <Navigate to="/dashboard" replace />;
  }
};

const PlayerAttendanceWrapper = () => {
  const isAdmin = authService.isAdmin();
  const isManager = authService.isManager();
  const isParent = authService.isParent();
  
  // Allow admins, managers, and coaches (managers includes coaches)
  // But exclude parents
  if ((isAdmin || isManager) && !isParent) {
    return <PlayerAttendance />;
  } else {
    return <Navigate to="/dashboard" replace />;
  }
};

const MembershipPlayersWrapper = () => {
  const isAdmin = authService.isAdmin();
  const isManager = authService.isManager();
  const isParent = authService.isParent();
  const userType = sessionStorage.getItem('user_type');

  if (isParent || userType === 'parent') {
    return <MembershipParent />;
  }
  if (isAdmin || isManager || userType === 'manager' || userType === 'coach' || authService.isSuperAdmin()) {
    return <MembershipPlayers />;
  }
  return <Navigate to="/dashboard" replace />;
};

// Suppress ResizeObserver loop warnings (harmless browser quirk)
// This error occurs when ResizeObserver callbacks trigger layout changes that cause more ResizeObserver callbacks
// It's a harmless browser quirk and safe to suppress
// Run immediately to catch errors as early as possible
(function() {
const isResizeObserverError = (message, error) => {
  if (!message) {
    // Check error object directly if message is not available
    if (error) {
      const errorStr = String(error);
      if (errorStr.includes('ResizeObserver loop')) return true;
      if (error?.stack && error.stack.includes('ResizeObserver loop')) return true;
    }
    return false;
  }
  const messageStr = String(message);
  return messageStr.includes('ResizeObserver loop') || 
         messageStr.includes('ResizeObserver loop limit exceeded') ||
         messageStr.includes('ResizeObserver loop completed');
};

// Suppress window.onerror
const originalError = window.onerror;
window.onerror = function(message, source, lineno, colno, error) {
  if (isResizeObserverError(message, error)) {
    return true; // Suppress the error
  }
  // Also check error stack trace
  if (error && error.stack && error.stack.includes('ResizeObserver loop')) {
    return true;
  }
  // Call original error handler if it exists
  if (originalError) {
    return originalError.call(this, message, source, lineno, colno, error);
  }
  return false;
};

// Suppress error events
window.addEventListener('error', (event) => {
  if (isResizeObserverError(event.message, event.error)) {
    event.preventDefault();
    event.stopPropagation();
    return true;
  }
  // Also check error object and stack trace
  if (event.error) {
    if (event.error.stack && event.error.stack.includes('ResizeObserver loop')) {
      event.preventDefault();
      event.stopPropagation();
      return true;
    }
    const errorStr = String(event.error);
    if (errorStr.includes('ResizeObserver loop')) {
      event.preventDefault();
      event.stopPropagation();
      return true;
    }
  }
  // Check filename/source if available
  if (event.filename && String(event.filename).includes('bundle.js')) {
    const messageStr = String(event.message || '');
    if (messageStr.includes('ResizeObserver loop')) {
      event.preventDefault();
      event.stopPropagation();
      return true;
    }
  }
}, true);

// Suppress unhandled promise rejections
window.addEventListener('unhandledrejection', (event) => {
  if (event.reason && isResizeObserverError(event.reason.message || event.reason)) {
    event.preventDefault();
    return true;
  }
});

// Suppress console.error (React DevTools and other libraries)
const originalConsoleError = console.error;
console.error = function(...args) {
  // Check all arguments for ResizeObserver errors
  const hasResizeObserverError = args.some(arg => {
    if (typeof arg === 'string') {
      return isResizeObserverError(arg);
    }
    if (arg && typeof arg === 'object') {
      // Check error objects
      if (arg.message && isResizeObserverError(arg.message, arg)) return true;
      if (arg.toString && isResizeObserverError(arg.toString(), arg)) return true;
      // Check stack trace
      if (arg.stack && arg.stack.includes('ResizeObserver loop')) return true;
    }
    return false;
  });
  
  // Also check if any argument contains ResizeObserver in its string representation
  const stringArgs = args.map(arg => {
    try {
      return String(arg);
    } catch {
      return '';
    }
  }).join(' ');
  if (stringArgs.includes('ResizeObserver loop')) {
    return; // Suppress the console error
  }
  
  if (hasResizeObserverError) {
    return; // Suppress the console error
  }
  originalConsoleError.apply(console, args);
};

// Suppress console.warn as well (some libraries use warn instead of error)
const originalConsoleWarn = console.warn;
console.warn = function(...args) {
  const hasResizeObserverError = args.some(arg => {
    if (typeof arg === 'string') {
      return isResizeObserverError(arg);
    }
    if (arg && typeof arg === 'object') {
      if (arg.message && isResizeObserverError(arg.message, arg)) return true;
      if (arg.toString && isResizeObserverError(arg.toString(), arg)) return true;
      if (arg.stack && arg.stack.includes('ResizeObserver loop')) return true;
    }
    return false;
  });
  
  // Also check string representation
  const stringArgs = args.map(arg => {
    try {
      return String(arg);
    } catch {
      return '';
    }
  }).join(' ');
  if (stringArgs.includes('ResizeObserver loop')) {
    return; // Suppress the console warning
  }
  
  if (hasResizeObserverError) {
    return; // Suppress the console warning
  }
  originalConsoleWarn.apply(console, args);
};
})(); // End of immediate execution wrapper

const container = document.getElementById('root');
const root = createRoot(container);

root.render(
  <React.StrictMode>
    <CacheBuster>
      <NotificationProvider>
        <HelmetProvider>
          <Router>
            <PushReminderHost />
            <ScrollToTop />
            <Routes>
              <Route path="/" element={<App />} />
              <Route path="/:teamId" element={<App />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/register" element={<Register />} />
              <Route path="/register-team-page" element={<RegisterTeamForm />} />
              <Route path="/register-parent" element={<ParentRegister />} />
              <Route path="/academy-welcome" element={<AcademyWelcomePage />} />
              <Route path="/friendly-welcome" element={<FriendlyWelcomePage />} />
              <Route path="/match-welcome" element={<MatchWelcomePage />} />
              <Route path="/player-sign-on" element={<PlayerSignOnPage />} />
              <Route path="/pro-signup" element={<ProSignupPage />} />
              <Route path="/pro-signup/parent" element={<ProSignupPage />} />
              <Route path="/payment-return" element={<PaymentReturn />} />
              <Route path="/payment-success" element={<PaymentSuccess />} />
              
              {/* Protected Routes */}
              <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
              <Route path="/match/:matchId" element={<ProtectedRoute><Match /></ProtectedRoute>} />
              <Route path="/match/:matchId/:tab" element={<ProtectedRoute><Match /></ProtectedRoute>} />
              <Route path="/training/:trainingId" element={<ProtectedRoute><Training /></ProtectedRoute>} />
              <Route path="/training/:trainingId/:tab" element={<ProtectedRoute><Training /></ProtectedRoute>} />
              <Route path="/leaderboard" element={
                <ProtectedRoute>
                  {isLeaderboardHidden()
                    ? <Navigate to="/dashboard" replace />
                    : <Leaderboard />}
                </ProtectedRoute>
              } />
              <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
              <Route path="/profile/:playerId" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
              <Route path="/matches" element={<ProtectedRoute><Matches /></ProtectedRoute>} />
              <Route path="/team/:teamId" element={<ProtectedRoute><TeamPage /></ProtectedRoute>} />
              <Route path="/edit-team/:teamId" element={<ProtectedRoute><EditTeam /></ProtectedRoute>} />
              <Route path="/fees" element={<ProtectedRoute><Fees /></ProtectedRoute>} />
              <Route path="/payments" element={<ProtectedRoute><Payments /></ProtectedRoute>} />
              <Route path="/playerCredits" element={<ProtectedRoute><PlayerCredits /></ProtectedRoute>} />
              <Route path="/notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />
              <Route path="/parent/article/:slug" element={<ProtectedRoute><ParentArticleView /></ProtectedRoute>} />
              <Route path="/parent/resources" element={<ProtectedRoute><ParentArticlesIndex /></ProtectedRoute>} />
              <Route path="/help" element={<ProtectedRoute><Help /></ProtectedRoute>} />
              
              {/* Admin Routes */}
              <Route path="/admin" element={
                <ProtectedRoute>
                  <AdminRouteWrapper />
                </ProtectedRoute>
              } />
              <Route path="/createMatch" element={
                <ProtectedRoute>
                  <CreateMatchWrapper />
                </ProtectedRoute>
              } />
              <Route path="/createTrainingSession" element={
                <ProtectedRoute>
                  <TrainingSessionWrapper />
                </ProtectedRoute>
              } />
              <Route path="/trainingSchedule" element={
                <ProtectedRoute>
                  <TrainingScheduleWrapper />
                </ProtectedRoute>
              } />
              <Route path="/players" element={
                <ProtectedRoute>
                  <PlayersWrapper />
                </ProtectedRoute>
              } />
              <Route path="/season-management" element={
                <ProtectedRoute>
                  <AdminRouteWrapper component={SeasonManagement} />
                </ProtectedRoute>
              } />
              <Route path="/team-settings" element={
                <ProtectedRoute>
                  <AdminRouteWrapper component={TeamSettings} />
                </ProtectedRoute>
              } />
              <Route path="/team-directory" element={
                <ProtectedRoute>
                  <TeamDirectoryWrapper />
                </ProtectedRoute>
              } />
              <Route path="/player-attendance" element={
                <ProtectedRoute>
                  <PlayerAttendanceWrapper />
                </ProtectedRoute>
              } />
              <Route path="/membership" element={
                <ProtectedRoute>
                  <MembershipPlayersWrapper />
                </ProtectedRoute>
              } />
              <Route path="/match-reports" element={<ProtectedRoute><MatchReportsPage /></ProtectedRoute>} />
              <Route path="/team-selection" element={<ProtectedRoute><TeamSelectionPage /></ProtectedRoute>} />
              <Route path="/player-signons" element={
                <ProtectedRoute>
                  <PlayerSignOnListPage />
                </ProtectedRoute>
              } />
              <Route path="/teamSelection/:matchId" element={
                <ProtectedRoute>
                  <TeamSelectionWrapper />
                </ProtectedRoute>
              } />
              <Route path="/player-matches" element={
                <ProtectedRoute>
                  <PlayerMatchesWrapper />
                </ProtectedRoute>
              } />
              
              {/* SuperAdmin Routes */}
              <Route path="/pitches" element={
                <ProtectedRoute>
                  <SuperAdminWrapper component={PitchList} />
                </ProtectedRoute>
              } />
              <Route path="/stripe-admin" element={
                <ProtectedRoute>
                  <SuperAdminWrapper component={StripeAdmin} />
                </ProtectedRoute>
              } />
              <Route path="/academy-subscriptions" element={
                <ProtectedRoute>
                  <AcademySubscriptions />
                </ProtectedRoute>
              } />
              <Route path="/academy-subscriptions/:id" element={
                <ProtectedRoute>
                  <AcademySubscriptionEdit />
                </ProtectedRoute>
              } />
              <Route path="/academy-payments" element={
                <ProtectedRoute>
                  <AcademyPayments />
                </ProtectedRoute>
              } />
              <Route path="/academy-payments/:id" element={
                <ProtectedRoute>
                  <AcademyPayments />
                </ProtectedRoute>
              } />
              <Route path="/academy-treasurer" element={
                <ProtectedRoute>
                  <AcademyTreasurerReports />
                </ProtectedRoute>
              } />
              <Route path="/academy-treasurer/:id" element={
                <ProtectedRoute>
                  <AcademyTreasurerReports />
                </ProtectedRoute>
              } />
              <Route path="/pitch/:pitchId" element={
                <ProtectedRoute>
                  <SuperAdminWrapper component={PitchDetails} />
                </ProtectedRoute>
              } />
              <Route path="/activity-log" element={
                <ProtectedRoute>
                  <SuperAdminWrapper component={ActivityLog} />
                </ProtectedRoute>
              } />
              <Route path="/push-notifications" element={
                <ProtectedRoute>
                  <SuperAdminWrapper component={PushNotificationAdmin} />
                </ProtectedRoute>
              } />
              <Route path="/push-notifications/test" element={
                <ProtectedRoute>
                  <SuperAdminWrapper component={PushNotificationTester} />
                </ProtectedRoute>
              } />
              <Route path="/register-team" element={
                <ProtectedRoute>
                  <SuperAdminWrapper component={RegisterTeam} />
                </ProtectedRoute>
              } />
              <Route path="/assign-manager" element={
                <ProtectedRoute>
                  <AssignManagerCoachWrapper />
                </ProtectedRoute>
              } />
              <Route path="/assign-club-secretary" element={
                <ProtectedRoute>
                  <SuperAdminWrapper component={AssignClubSecretary} />
                </ProtectedRoute>
              } />
              <Route path="/register-player/:teamId" element={
                <ProtectedRoute>
                  <SuperAdminWrapper component={RegisterPlayer} />
                </ProtectedRoute>
              } />
              <Route path="/stories-manager" element={
                <ProtectedRoute>
                  <StoryManagerWrapper />
                </ProtectedRoute>
              } />
              <Route path="/product-news" element={
                <ProtectedRoute>
                  <SuperAdminWrapper component={ProductNewsManager} />
                </ProtectedRoute>
              } />
              <Route path="/my-account" element={<ProtectedRoute><MyAccount /></ProtectedRoute>} />
              <Route path="/parent/settings" element={<ProtectedRoute><ParentSettings /></ProtectedRoute>} />
            </Routes>
            <ConditionalBottomNav />
          </Router>
        </HelmetProvider>
      </NotificationProvider>
    </CacheBuster>
  </React.StrictMode>
);

{/* <Route 
  path="/dashboard" 
  element={
    <ProtectedRoute>
      <Dashboard />
    </ProtectedRoute>
  } 
/> */}